import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { Modal, Form, Button, notification } from 'antd';

import GroupReportForm from '../GroupReportForm';
import { ModalProps } from '@/types/ModalProps';
import { ReportGroupsFormValues } from '@/core/domain/reports/stock/Report.types';
import { useGenerateGroupsReport } from '@/core/domain/reports/stock/Report.hooks';
import { useDefaultFormErrorNotification, useReportGenerate } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import SpaceForButton from '@/components/_common/SpaceForButtons';

function GroupReportModal({ visible, handleVisible }: ModalProps) {
  const intl = useIntl();
  const mutation = useGenerateGroupsReport();
  const [form] = Form.useForm<ReportGroupsFormValues>();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();
  const report = useReportGenerate();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const values = await form.validateFields();

      const obj = {
        ...values,
        active: values.active ?? undefined,
        format: FILE_FORMAT[format],
      };

      const result = await mutation.mutateAsync(obj);

      if (callback) {
        callback(result);
      }

      notification.success({
        message: intl.formatMessage({
          id: 'groups.notification.generate.report.title',
        }),
        description: intl.formatMessage({
          id: 'groups.notification.generate.report.description',
        }),
      });
      handleCancel();
    } catch (err) {
      sendFormErrorNotification(err);
    }
    setFileUnloading(format);
  };

  function handleCancel() {
    form.resetFields();
    handleVisible();
  }

  return (
    <Modal
      width={660}
      open={visible}
      onCancel={handleCancel}
      title={intl.formatMessage({ id: 'consumables.modal.report.title' })}
      footer={
        <SpaceForButton justifyContent="flex-end">
          <Button
            type="primary"
            block
            onClick={() =>
              handleGenerateReport('REPORT_VIEW', (result) =>
                report.openFile({
                  result,
                  fileName: 'Grupos.pdf',
                  fileType: 'application/pdf',
                }),
              )
            }
            key="viewReport"
            loading={fileLoading.REPORT_VIEW}
          >
            <FormattedMessage id="consumables.modal.report.buttons.viewReport" />
          </Button>
          <Button
            type="primary"
            key="PDF"
            block
            onClick={() =>
              handleGenerateReport('PDF', (result) =>
                report.downloadFile({ result, fileName: 'Grupos.pdf' }),
              )
            }
            loading={fileLoading.PDF}
          >
            <FormattedMessage id="consumables.modal.report.buttons.pdf" />
          </Button>
          <Button
            type="primary"
            key="CSV"
            block
            onClick={() =>
              handleGenerateReport('CSV', (result) =>
                report.downloadFile({ result, fileName: 'Grupos.csv' }),
              )
            }
          >
            <FormattedMessage id="consumables.modal.report.buttons.csv" />
          </Button>
        </SpaceForButton>
      }
    >
      <GroupReportForm form={form} />
    </Modal>
  );
}

export default GroupReportModal;
